import { Loader } from "@googlemaps/js-api-loader";
import {
  GOOGLE_MAPS_JS_API_LIBRARIES,
  GOOGLE_MAPS_VERSION,
} from "./googleMapsConfig";
import { OVERLAY_POPUP_Z_INDEX_FALLBACK } from "../components/dialogs/overlayPopupLayer";

const PAC_INPUT_SELECTOR = ".pac-target-input";
const PAC_CONTAINER_SELECTOR = ".pac-container";
const PAC_Z_INDEX = `var(--ll-overlay-popup-z-index, ${OVERLAY_POPUP_Z_INDEX_FALLBACK})`;
const GOOGLE_PLACES_ACTIVATION_EVENT_TYPES = new Set([
  "click",
  "keydown",
  "mousedown",
  "pointerdown",
  "touchstart",
]);

let placesLoaderPromise = null;
let placesLoaderKey = "";
const popupOwners = new WeakMap();
const pendingPopupOwners = new Set();

const isAutocompleteInput = (value) =>
  !!value &&
  typeof value.matches === "function" &&
  value.matches(PAC_INPUT_SELECTOR);

export const hasGooglePlacesLoadActivation = ({
  event = null,
  windowRef = typeof window === "undefined" ? null : window,
} = {}) => {
  const isTestWindow =
    process.env.NODE_ENV === "test" &&
    typeof window !== "undefined" &&
    windowRef === window;
  if (isTestWindow) return true;

  if (!event) return false;

  if (event?.isTrusted === false) return false;

  const eventType = String(event?.type || event?.nativeEvent?.type || "");
  if (!GOOGLE_PLACES_ACTIVATION_EVENT_TYPES.has(eventType)) return false;

  const userActivation = windowRef?.navigator?.userActivation;
  if (!userActivation) return true;

  return Boolean(userActivation.isActive || userActivation.hasBeenActive);
};

const writeStyle = (element, property, value, priority = "") => {
  if (
    element.style.getPropertyValue(property) === value &&
    element.style.getPropertyPriority(property) === priority
  ) {
    return;
  }

  element.style.setProperty(property, value, priority);
};

const getInputPopupIds = (input) =>
  ["aria-controls", "aria-owns"]
    .flatMap((attributeName) =>
      String(input?.getAttribute?.(attributeName) || "")
        .split(/\s+/)
        .filter(Boolean),
    )
    .filter((id, index, ids) => ids.indexOf(id) === index);

export const positionGooglePlacesAutocompleteContainers = ({
  input,
  containers = [],
  windowRef = typeof window === "undefined" ? null : window,
} = {}) => {
  if (!input || !windowRef || typeof input.getBoundingClientRect !== "function") {
    return false;
  }

  const rect = input.getBoundingClientRect();
  if (
    !Number.isFinite(rect.left) ||
    !Number.isFinite(rect.bottom) ||
    !Number.isFinite(rect.width) ||
    rect.width <= 0
  ) {
    return false;
  }

  const scrollX = Number(windowRef.scrollX || windowRef.pageXOffset || 0);
  const scrollY = Number(windowRef.scrollY || windowRef.pageYOffset || 0);
  const left = `${rect.left + scrollX}px`;
  const top = `${rect.bottom + scrollY}px`;
  const width = `${rect.width}px`;

  containers.filter(Boolean).forEach((container) => {
    writeStyle(container, "left", left);
    writeStyle(container, "top", top);
    writeStyle(container, "width", width);
    writeStyle(container, "z-index", PAC_Z_INDEX, "important");
  });

  return containers.length > 0;
};

export const loadGooglePlacesLibrary = ({
  apiKey = process.env.REACT_APP_GOOGLE_API_KEY,
  activationEvent = null,
  requireUserActivation = false,
  LoaderClass = Loader,
  windowRef = typeof window === "undefined" ? null : window,
} = {}) => {
  if (
    requireUserActivation &&
    !hasGooglePlacesLoadActivation({ event: activationEvent, windowRef })
  ) {
    return Promise.resolve(null);
  }

  if (windowRef?.google?.maps?.places?.Autocomplete) {
    return Promise.resolve(windowRef.google.maps.places);
  }
  if (!apiKey) {
    return Promise.reject(new Error("Google Places API key is unavailable."));
  }

  const loaderKey = `${apiKey}:${GOOGLE_MAPS_VERSION}:${GOOGLE_MAPS_JS_API_LIBRARIES.join(",")}`;
  if (placesLoaderPromise && placesLoaderKey === loaderKey) {
    return placesLoaderPromise;
  }

  const loader = new LoaderClass({
    apiKey,
    version: GOOGLE_MAPS_VERSION,
    libraries: GOOGLE_MAPS_JS_API_LIBRARIES,
  });
  placesLoaderKey = loaderKey;
  placesLoaderPromise = Promise.resolve()
    .then(() => {
      if (typeof loader.importLibrary === "function") {
        return loader.importLibrary("places");
      }
      if (typeof loader.load === "function") {
        return loader.load();
      }
      throw new Error("Google Places loader is unavailable.");
    })
    .then(
      (library) =>
        library ||
        windowRef?.google?.maps?.places ||
        Promise.reject(new Error("Google Places failed to initialize.")),
    )
    .catch((error) => {
      placesLoaderPromise = null;
      placesLoaderKey = "";
      throw error;
    });

  return placesLoaderPromise;
};

export const mountGooglePlacesAutocomplete = ({
  input,
  options = {},
  onPlaceChanged = () => {},
  onError = () => {},
  apiKey = process.env.REACT_APP_GOOGLE_API_KEY,
  activationEvent = null,
  requireUserActivation = true,
  LoaderClass = Loader,
  documentRef = typeof document === "undefined" ? null : document,
  windowRef = typeof window === "undefined" ? null : window,
} = {}) => {
  let disposed = false;
  let autocomplete = null;
  let listener = null;
  let ownedContainers = [];
  let previousContainers = [];
  let widgetCreated = false;
  let ownershipObserver = null;
  const ownerToken = {};
  const inputValueWhenMounted = String(input?.value || "");
  const pendingOwner = {
    documentRef,
    get disposed() {
      return disposed;
    },
    get widgetCreated() {
      return widgetCreated;
    },
  };

  const claimContainer = (container) => {
    if (!container || popupOwners.has(container)) return false;

    popupOwners.set(container, ownerToken);
    ownedContainers.push(container);
    pendingPopupOwners.delete(pendingOwner);
    ownershipObserver?.disconnect();
    ownershipObserver = null;
    return true;
  };

  const captureOwnedContainers = () => {
    if (!documentRef || !widgetCreated) return;
    const associatedContainer = getInputPopupIds(input)
      .map((id) => documentRef.getElementById?.(id))
      .find(
        (container) =>
          container?.matches?.(PAC_CONTAINER_SELECTOR) &&
          !previousContainers.includes(container) &&
          !popupOwners.has(container),
      );
    if (claimContainer(associatedContainer)) return;

    const availableContainers = Array.from(
      documentRef.querySelectorAll(PAC_CONTAINER_SELECTOR),
    ).filter(
      (container) =>
        !previousContainers.includes(container) &&
        !popupOwners.has(container),
    );
    const activePendingOwners = Array.from(pendingPopupOwners).filter(
      (owner) =>
        owner.documentRef === documentRef &&
        owner.widgetCreated &&
        !owner.disposed,
    );
    if (activePendingOwners.length === 1) {
      claimContainer(availableContainers[0]);
    }
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;

    listener?.remove?.();
    listener = null;
    ownershipObserver?.disconnect();
    ownershipObserver = null;
    pendingPopupOwners.delete(pendingOwner);
    autocomplete = null;
    ownedContainers.forEach((container) => {
      if (popupOwners.get(container) === ownerToken) {
        popupOwners.delete(container);
        container?.remove?.();
      }
    });
    ownedContainers = [];
    widgetCreated = false;
  };

  const ready = loadGooglePlacesLibrary({
    apiKey,
    activationEvent,
    requireUserActivation,
    LoaderClass,
    windowRef,
  })
    .then((places) => {
      if (!places) return null;
      if (disposed || !input?.isConnected) return null;

      const Autocomplete =
        places?.Autocomplete || windowRef?.google?.maps?.places?.Autocomplete;
      if (typeof Autocomplete !== "function") {
        throw new Error("Google Places Autocomplete is unavailable.");
      }

      previousContainers = documentRef
        ? Array.from(documentRef.querySelectorAll(PAC_CONTAINER_SELECTOR))
        : [];
      pendingPopupOwners.add(pendingOwner);
      if (
        documentRef?.body &&
        typeof windowRef?.MutationObserver === "function"
      ) {
        ownershipObserver = new windowRef.MutationObserver(
          captureOwnedContainers,
        );
        ownershipObserver.observe(documentRef.body, {
          attributes: true,
          attributeFilter: ["aria-controls", "aria-owns"],
          childList: true,
          subtree: true,
        });
      }
      autocomplete = new Autocomplete(input, options);
      widgetCreated = true;
      captureOwnedContainers();
      listener = autocomplete.addListener?.("place_changed", () => {
        if (!disposed) onPlaceChanged(autocomplete);
      });
      const currentInputValue = String(input?.value || "");
      if (
        currentInputValue &&
        currentInputValue !== inputValueWhenMounted &&
        typeof input?.dispatchEvent === "function" &&
        typeof windowRef?.Event === "function"
      ) {
        input.dispatchEvent(new windowRef.Event("input", { bubbles: true }));
      }
      return autocomplete;
    })
    .catch((error) => {
      const shouldReportError = !disposed;
      dispose();
      if (shouldReportError) onError(error);
      throw error;
    });

  return {
    dispose,
    getAutocomplete: () => autocomplete,
    getOwnedContainers: () => [...ownedContainers],
    ready,
  };
};

const getObservedAncestors = (input, documentRef) => {
  const ancestors = [];
  let current = input;

  while (current && current !== documentRef?.body) {
    ancestors.push(current);
    current = current.parentElement;
  }

  return ancestors;
};

export const createGooglePlacesAutocompleteOverlayManager = ({
  documentRef = typeof document === "undefined" ? null : document,
  windowRef = typeof window === "undefined" ? null : window,
} = {}) => {
  if (!documentRef || !windowRef) {
    return { cleanup: () => {}, sync: () => false };
  }

  let activeInput = null;
  let animationFrame = null;
  let mutationObserver = null;
  let resizeObserver = null;
  const visualViewport = windowRef.visualViewport || null;

  const getContainers = () =>
    Array.from(documentRef.querySelectorAll(PAC_CONTAINER_SELECTOR));

  const sync = () => {
    animationFrame = null;
    if (!activeInput?.isConnected) {
      activeInput = null;
      return false;
    }

    return positionGooglePlacesAutocompleteContainers({
      input: activeInput,
      containers: getContainers(),
      windowRef,
    });
  };

  const scheduleSync = () => {
    if (animationFrame !== null) return;

    if (typeof windowRef.requestAnimationFrame === "function") {
      animationFrame = windowRef.requestAnimationFrame(sync);
      return;
    }

    animationFrame = windowRef.setTimeout(sync, 0);
  };

  const observeActiveInputLayout = () => {
    resizeObserver?.disconnect();
    if (!activeInput || typeof windowRef.ResizeObserver !== "function") return;

    resizeObserver = new windowRef.ResizeObserver(scheduleSync);
    getObservedAncestors(activeInput, documentRef).forEach((element) =>
      resizeObserver.observe(element),
    );
  };

  const activateInput = (event) => {
    if (!isAutocompleteInput(event?.target)) return;
    if (activeInput !== event.target) {
      activeInput = event.target;
      observeActiveInputLayout();
    }
    scheduleSync();
  };

  ["focusin", "input", "keydown", "pointerdown"].forEach((eventName) => {
    documentRef.addEventListener(eventName, activateInput, true);
  });
  documentRef.addEventListener("scroll", scheduleSync, true);
  windowRef.addEventListener("resize", scheduleSync);
  visualViewport?.addEventListener?.("resize", scheduleSync);
  visualViewport?.addEventListener?.("scroll", scheduleSync);

  if (typeof windowRef.MutationObserver === "function" && documentRef.body) {
    mutationObserver = new windowRef.MutationObserver(scheduleSync);
    mutationObserver.observe(documentRef.body, {
      attributes: true,
      attributeFilter: ["class", "style"],
      childList: true,
      subtree: true,
    });
  }

  const cleanup = () => {
    ["focusin", "input", "keydown", "pointerdown"].forEach((eventName) => {
      documentRef.removeEventListener(eventName, activateInput, true);
    });
    documentRef.removeEventListener("scroll", scheduleSync, true);
    windowRef.removeEventListener("resize", scheduleSync);
    visualViewport?.removeEventListener?.("resize", scheduleSync);
    visualViewport?.removeEventListener?.("scroll", scheduleSync);
    mutationObserver?.disconnect();
    resizeObserver?.disconnect();

    if (animationFrame !== null) {
      if (typeof windowRef.cancelAnimationFrame === "function") {
        windowRef.cancelAnimationFrame(animationFrame);
      } else {
        windowRef.clearTimeout(animationFrame);
      }
    }

    activeInput = null;
    animationFrame = null;
  };

  return { cleanup, sync };
};

let installedManager = null;

export const installGooglePlacesAutocompleteOverlayManager = () => {
  if (installedManager) return installedManager;
  installedManager = createGooglePlacesAutocompleteOverlayManager();
  return installedManager;
};

export const resetGooglePlacesAutocompleteOverlayManagerForTests = () => {
  installedManager?.cleanup();
  installedManager = null;
  placesLoaderPromise = null;
  placesLoaderKey = "";
  pendingPopupOwners.clear();
};
